import { Wrench, Layers, LayoutTemplate } from 'lucide-react';
import { cn } from '../utils';
import { Tooltip } from '../ui';
import { useEditorStore } from '../store';
import { ToolsPanel } from './panels/ToolsPanel';
import { BlocksPanel } from './panels/BlocksPanel';
import { LayersPanel } from './panels/LayersPanel';
import type { PanelTab } from '../types/editor.types';

const TABS = [
  { id: 'tools'  as PanelTab, label: 'Elements', icon: Wrench },
  { id: 'blocks' as PanelTab, label: 'My Blocks', icon: LayoutTemplate },
  { id: 'layers' as PanelTab, label: 'Layers',   icon: Layers },
];

const PANEL_META: Record<PanelTab, { title: string; sub: string; icon: React.ElementType }> = {
  tools:  { title: 'Elements',   sub: 'Drag onto canvas',  icon: Wrench },
  blocks: { title: 'My Blocks',  sub: 'Reusable sections', icon: LayoutTemplate },
  layers: { title: 'Layer Tree', sub: 'Design structure',  icon: Layers },
};

export function LeftPanel() {
  const { activePanel, setActivePanel } = useEditorStore();
  const meta = PANEL_META[activePanel];
  const PanelIcon = meta.icon;

  return (
    <aside className="flex shrink-0 h-full">

      {/* ── Minimal icon rail ── */}
      <div className="w-12 flex flex-col items-center pt-3 pb-4 gap-1
        bg-surface dark:bg-dark-900
        border-r border-line/60 dark:border-dark-700/60">
        {TABS.map(({ id, label, icon: Icon }) => {
          const isActive = activePanel === id;
          return (
            <Tooltip key={id} content={label} side="right">
              <button
                onClick={() => setActivePanel(id)}
                className={cn(
                  'relative w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-150',
                  isActive
                    ? 'bg-violet-600 text-white shadow-sm shadow-violet-500/25'
                    : 'text-content-subtle dark:text-dark-500 hover:bg-gray-100 dark:hover:bg-dark-800 hover:text-gray-700 dark:hover:text-dark-200'
                )}
              >
                <Icon className="size-[17px]" />
                {isActive && (
                  <span className="absolute -right-px top-2 bottom-2 w-[2px] bg-violet-500 rounded-full" />
                )}
              </button>
            </Tooltip>
          );
        })}
      </div>

      {/* ── Panel content ── */}
      <div className="w-56 flex flex-col min-w-0
        bg-surface dark:bg-dark-800
        border-r border-line/60 dark:border-dark-700">

        {/* Compact structured header */}
        <div className="flex items-center gap-2.5 px-3 py-2.5 shrink-0
          bg-surface dark:bg-dark-800
          border-b border-line-subtle dark:border-dark-700/60">
          <div className="w-6 h-6 rounded-lg bg-violet-100 dark:bg-violet-900/30
            flex items-center justify-center shrink-0">
            <PanelIcon className="size-3.5 text-violet-600 dark:text-violet-400" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-bold text-gray-800 dark:text-white leading-none">{meta.title}</p>
            <p className="text-[10px] text-content-subtle dark:text-dark-500 mt-0.5">{meta.sub}</p>
          </div>
        </div>

        {/* Panel body */}
        <div className="flex-1 overflow-hidden">
          {activePanel === 'tools'  && <ToolsPanel />}
          {activePanel === 'blocks' && <BlocksPanel />}
          {activePanel === 'layers' && <LayersPanel />}
        </div>
      </div>
    </aside>
  );
}
